import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Loader2, Eye, EyeOff, Cpu, Mail, Lock, AlertCircle, User, ArrowLeft, CheckCircle2 } from 'lucide-react';

// Inline SVG icons for OAuth providers
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function GithubIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" />
    </svg>
  );
}

// Email validation
function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

type AuthMode = 'login' | 'register' | 'forgot';

export default function PublicLogin() {
  const { login, signUp, loginWithGoogle, loginWithGithub, sendPasswordReset } = useAuth();

  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);

  const clearState = (nextMode: AuthMode) => {
    setMode(nextMode);
    setError('');
    setSuccessMsg('');
    setFieldErrors({});
    setPassword('');
    setConfirmPassword('');
  };

  // ─── Form validation ──────────────────────────────────────────────────────
  const validateLogin = () => {
    const errs: Record<string, string> = {};
    if (!email.trim()) errs.email = 'Email is required';
    else if (!isValidEmail(email)) errs.email = 'Please enter a valid email address';
    if (!password) errs.password = 'Password is required';
    else if (password.length < 6) errs.password = 'Password must be at least 6 characters';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateRegister = () => {
    const errs: Record<string, string> = {};
    if (!displayName.trim()) errs.displayName = 'Full name is required';
    if (!email.trim()) errs.email = 'Email is required';
    else if (!isValidEmail(email)) errs.email = 'Please enter a valid email address';
    if (!password) errs.password = 'Password is required';
    else if (password.length < 6) errs.password = 'Password must be at least 6 characters';
    if (!confirmPassword) errs.confirmPassword = 'Please confirm your password';
    else if (password !== confirmPassword) errs.confirmPassword = 'Passwords do not match';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateForgot = () => {
    const errs: Record<string, string> = {};
    if (!email.trim()) errs.email = 'Email is required';
    else if (!isValidEmail(email)) errs.email = 'Please enter a valid email address';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!validateLogin()) return;
    setIsLoading(true);
    setLoadingProvider('email');
    const result = await login(email, password);
    if (!result.success) setError(result.error || 'Login failed');
    setIsLoading(false);
    setLoadingProvider(null);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!validateRegister()) return;
    setIsLoading(true);
    setLoadingProvider('email');
    const result = await signUp(email, password, displayName);
    if (!result.success) setError(result.error || 'Registration failed');
    setIsLoading(false);
    setLoadingProvider(null);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!validateForgot()) return;
    setIsLoading(true);
    setLoadingProvider('email');
    const result = await sendPasswordReset(email);
    if (result.success) {
      setSuccessMsg(`A password reset link has been sent to ${email}. Check your inbox.`);
    } else {
      setError(result.error || 'Failed to send reset email');
    }
    setIsLoading(false);
    setLoadingProvider(null);
  };

  const handleGoogleLogin = async () => {
    setError('');
    setFieldErrors({});
    setIsLoading(true);
    setLoadingProvider('google');
    const result = await loginWithGoogle();
    if (!result.success) setError(result.error || 'Google sign-in failed');
    setIsLoading(false);
    setLoadingProvider(null);
  };

  const handleGithubLogin = async () => {
    setError('');
    setFieldErrors({});
    setIsLoading(true);
    setLoadingProvider('github');
    const result = await loginWithGithub();
    if (!result.success) setError(result.error || 'GitHub sign-in failed');
    setIsLoading(false);
    setLoadingProvider(null);
  };

  const cardTitle =
    mode === 'login' ? 'Sign in to your account'
      : mode === 'register' ? 'Create your account'
        : 'Reset your password';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center relative overflow-hidden p-4">
      {/* Background grid */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(oklch(var(--electric-blue)) 1px, transparent 1px),
            linear-gradient(90deg, oklch(var(--electric-blue)) 1px, transparent 1px)
          `,
          backgroundSize: '48px 48px',
        }}
      />
      {/* Glow orbs */}
      <div
        className="absolute top-1/4 left-1/3 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-[0.06] blur-[100px] pointer-events-none"
        style={{ background: 'oklch(var(--electric-blue))' }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full opacity-[0.04] blur-[80px] pointer-events-none"
        style={{ background: 'oklch(var(--success))' }}
      />

      <div className="relative z-10 w-full max-w-[420px]">
        {/* Branding */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-xl bg-electric-blue/15 border border-electric-blue/25 flex items-center justify-center">
              <Cpu className="w-8 h-8 text-electric-blue" />
            </div>
          </div>
          <h1 className="text-3xl font-black tracking-wider text-electric-blue uppercase">
            VisionInspect
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5 tracking-wide">
            AI-powered quality inspection platform
          </p>
        </div>

        {/* Auth Card */}
        <div className="bg-card border border-border rounded-xl shadow-card-dark overflow-hidden backdrop-blur-sm">
          {/* Card header */}
          <div className="px-6 py-4 border-b border-border bg-muted/20 flex items-center gap-2">
            {mode !== 'login' && (
              <button
                type="button"
                onClick={() => clearState('login')}
                className="text-muted-foreground hover:text-foreground transition-colors mr-1"
                aria-label="Back to login"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <p className="text-sm font-semibold tracking-wider uppercase text-foreground flex-1 text-center pr-5">
              {cardTitle}
            </p>
          </div>

          <div className="p-6 space-y-5">
            {/* Alert messages */}
            {error && (
              <div className="flex items-start gap-2.5 bg-status-danger/10 border border-status-danger/25 rounded-lg px-4 py-3 text-sm text-status-danger animate-in fade-in duration-200">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            {successMsg && (
              <div className="flex items-start gap-2.5 bg-status-success/10 border border-status-success/25 rounded-lg px-4 py-3 text-sm text-status-success animate-in fade-in duration-200">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{successMsg}</span>
              </div>
            )}

            {/* ─── LOGIN FORM ──────────────────────────────────────────── */}
            {mode === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4">
                <FormField
                  id="login-email" label="Email Address" type="email"
                  value={email} onChange={e => { setEmail(e.target.value); setFieldErrors(p => ({ ...p, email: '' })); }}
                  placeholder="you@example.com" autoComplete="email"
                  icon={<Mail className="w-4 h-4" />} error={fieldErrors.email}
                />
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label htmlFor="login-password" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      Password
                    </label>
                    <button
                      type="button"
                      className="text-xs text-electric-blue hover:text-electric-blue/80 transition-colors font-medium"
                      onClick={() => clearState('forgot')}
                    >
                      Forgot Password?
                    </button>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    <input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => { setPassword(e.target.value); setFieldErrors(p => ({ ...p, password: '' })); }}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      className={`w-full bg-input border rounded-lg pl-10 pr-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-electric-blue/50 focus:border-electric-blue transition-all ${fieldErrors.password ? 'border-status-danger' : 'border-border'}`}
                    />
                    <button type="button" onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" tabIndex={-1}>
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {fieldErrors.password && <p className="text-xs text-status-danger mt-1 pl-1">{fieldErrors.password}</p>}
                </div>
                <button type="submit" disabled={isLoading}
                  className="w-full bg-electric-blue text-background font-bold py-2.5 rounded-lg text-sm tracking-wider uppercase transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 glow-blue">
                  {loadingProvider === 'email' ? (<><Loader2 className="w-4 h-4 animate-spin" />Signing in...</>) : 'Sign In'}
                </button>
              </form>
            )}

            {/* ─── REGISTER FORM ───────────────────────────────────────── */}
            {mode === 'register' && (
              <form onSubmit={handleRegister} className="space-y-4">
                <FormField
                  id="reg-name" label="Full Name" type="text"
                  value={displayName} onChange={e => { setDisplayName(e.target.value); setFieldErrors(p => ({ ...p, displayName: '' })); }}
                  placeholder="Jane Smith" autoComplete="name"
                  icon={<User className="w-4 h-4" />} error={fieldErrors.displayName}
                />
                <FormField
                  id="reg-email" label="Email Address" type="email"
                  value={email} onChange={e => { setEmail(e.target.value); setFieldErrors(p => ({ ...p, email: '' })); }}
                  placeholder="you@example.com" autoComplete="email"
                  icon={<Mail className="w-4 h-4" />} error={fieldErrors.email}
                />
                <PasswordField
                  id="reg-password" label="Password"
                  value={password} onChange={e => { setPassword(e.target.value); setFieldErrors(p => ({ ...p, password: '' })); }}
                  show={showPassword} onToggle={() => setShowPassword(v => !v)}
                  placeholder="At least 6 characters" autoComplete="new-password"
                  error={fieldErrors.password}
                />
                <PasswordField
                  id="reg-confirm" label="Confirm Password"
                  value={confirmPassword} onChange={e => { setConfirmPassword(e.target.value); setFieldErrors(p => ({ ...p, confirmPassword: '' })); }}
                  show={showConfirm} onToggle={() => setShowConfirm(v => !v)}
                  placeholder="Repeat your password" autoComplete="new-password"
                  error={fieldErrors.confirmPassword}
                />
                <button type="submit" disabled={isLoading}
                  className="w-full bg-electric-blue text-background font-bold py-2.5 rounded-lg text-sm tracking-wider uppercase transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 glow-blue">
                  {loadingProvider === 'email' ? (<><Loader2 className="w-4 h-4 animate-spin" />Creating account...</>) : 'Create Account'}
                </button>
              </form>
            )}

            {/* ─── FORGOT PASSWORD FORM ────────────────────────────────── */}
            {mode === 'forgot' && (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
                <FormField
                  id="forgot-email" label="Email Address" type="email"
                  value={email} onChange={e => { setEmail(e.target.value); setFieldErrors(p => ({ ...p, email: '' })); }}
                  placeholder="you@example.com" autoComplete="email"
                  icon={<Mail className="w-4 h-4" />} error={fieldErrors.email}
                />
                <button type="submit" disabled={isLoading || !!successMsg}
                  className="w-full bg-electric-blue text-background font-bold py-2.5 rounded-lg text-sm tracking-wider uppercase transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 glow-blue">
                  {loadingProvider === 'email' ? (<><Loader2 className="w-4 h-4 animate-spin" />Sending...</>) : 'Send Reset Link'}
                </button>
              </form>
            )}

            {/* ─── OAuth buttons (only for login / register) ────────────── */}
            {mode !== 'forgot' && (
              <>
                <div className="relative flex items-center gap-3">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider whitespace-nowrap">or continue with</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" onClick={handleGoogleLogin} disabled={isLoading}
                    className="flex items-center justify-center gap-2 bg-input border border-border rounded-lg py-2.5 px-3 text-sm font-medium text-foreground transition-all hover:bg-accent hover:border-electric-blue/30 disabled:opacity-50 disabled:cursor-not-allowed">
                    {loadingProvider === 'google' ? <Loader2 className="w-4 h-4 animate-spin" /> : <GoogleIcon className="w-4 h-4" />}
                    <span>Google</span>
                  </button>
                  <button type="button" onClick={handleGithubLogin} disabled={isLoading}
                    className="flex items-center justify-center gap-2 bg-input border border-border rounded-lg py-2.5 px-3 text-sm font-medium text-foreground transition-all hover:bg-accent hover:border-electric-blue/30 disabled:opacity-50 disabled:cursor-not-allowed">
                    {loadingProvider === 'github' ? <Loader2 className="w-4 h-4 animate-spin" /> : <GithubIcon className="w-4 h-4" />}
                    <span>GitHub</span>
                  </button>
                </div>
              </>
            )}

            {/* ─── Mode switch link ─────────────────────────────────────── */}
            {mode === 'login' && (
              <p className="text-center text-sm text-muted-foreground">
                Don't have an account?{' '}
                <button type="button" onClick={() => clearState('register')}
                  className="text-electric-blue hover:text-electric-blue/80 font-medium transition-colors">
                  Create Account
                </button>
              </p>
            )}
            {mode === 'register' && (
              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <button type="button" onClick={() => clearState('login')}
                  className="text-electric-blue hover:text-electric-blue/80 font-medium transition-colors">
                  Sign In
                </button>
              </p>
            )}
            {mode === 'forgot' && successMsg && (
              <p className="text-center text-sm text-muted-foreground">
                <button type="button" onClick={() => clearState('login')}
                  className="text-electric-blue hover:text-electric-blue/80 font-medium transition-colors">
                  Back to Sign In
                </button>
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 flex items-center justify-center">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="w-1.5 h-1.5 rounded-full bg-status-success glow-green" />
            <Cpu className="w-3 h-3" />
            <span>System: <span className="text-status-success">Secure</span></span>
          </div>
        </div>
        <p className="text-center text-xs text-muted-foreground/50 mt-4 font-mono tracking-wide">
          © {new Date().getFullYear()} VisionInspect · All rights reserved
        </p>
      </div>
    </div>
  );
}

// ─── Reusable form field components ─────────────────────────────────────────
function FormField({
  id, label, type, value, onChange, placeholder, autoComplete, icon, error,
}: {
  id: string; label: string; type: string;
  value: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string; autoComplete?: string;
  icon?: React.ReactNode; error?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</label>
      <div className="relative">
        {icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">{icon}</span>}
        <input
          id={id} type={type} value={value} onChange={onChange}
          placeholder={placeholder} autoComplete={autoComplete}
          className={`w-full bg-input border rounded-lg ${icon ? 'pl-10' : 'pl-3'} pr-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-electric-blue/50 focus:border-electric-blue transition-all ${error ? 'border-status-danger' : 'border-border'}`}
        />
      </div>
      {error && <p className="text-xs text-status-danger mt-1 pl-1">{error}</p>}
    </div>
  );
}

function PasswordField({
  id, label, value, onChange, show, onToggle, placeholder, autoComplete, error,
}: {
  id: string; label: string;
  value: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  show: boolean; onToggle: () => void;
  placeholder?: string; autoComplete?: string; error?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</label>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <input
          id={id} type={show ? 'text' : 'password'} value={value} onChange={onChange}
          placeholder={placeholder} autoComplete={autoComplete}
          className={`w-full bg-input border rounded-lg pl-10 pr-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-electric-blue/50 focus:border-electric-blue transition-all ${error ? 'border-status-danger' : 'border-border'}`}
        />
        <button type="button" onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" tabIndex={-1}>
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {error && <p className="text-xs text-status-danger mt-1 pl-1">{error}</p>}
    </div>
  );
}
